/**
 * Trash (L2): entries with soft-delete. Restore = clear deleted_at
 * (normal write via the write-path); "delete permanently" = physical
 * deletion (irreversible, cascades photos/tags/versions).
 */
import { createRoute } from "@tanstack/solid-router";
import { msg } from "@lingui/core/macro";
import { createSignal, For, Show } from "solid-js";
import { RotateCcw, Trash2 } from "@fleet/icons";
import { useDeletedEntriesQuery } from "@journal/shared/solid";
import { TRASH_PURGE_DAYS } from "@journal/shared";
import type { JournalEntry } from "@journal/powersync";
import { appRoute } from "./route";
import { PageContainer, Skeleton } from "@fleet/ui";
import { EmptyState } from "@/components/ui/empty-state";
import { Button } from "@/components/ui/button";
import { ConfirmDialog } from "@/components/ui/confirm-dialog";
import { useI18n } from "@/providers/i18n-provider";
import { formatDayShort, formatRelativeTime } from "@/features/journal/format";
import { useJournalData } from "@/features/journal/hooks/use-journal-data";
import { useEntryMutations } from "@/features/journal/hooks/use-entry-mutations";

const PREVIEW_MAX_CHARS = 160;
const MS_PER_DAY = 86_400_000;

function preview(text: string | null): string {
  const clean = (text ?? "").replaceAll(/\s+/gu, " ").trim();
  return clean.length > PREVIEW_MAX_CHARS ? `${clean.slice(0, PREVIEW_MAX_CHARS)}…` : clean;
}

/**
 * SOFT_DELETE_STANDARD — countdown "will be permanently deleted in Y days" =
 * deleted_at + TRASH_PURGE_DAYS − now. Returns the remaining days (minimum 0) or
 * null if there is no valid date. The actual purge is done by the server job.
 */
function daysUntilPurge(iso: string | null): number | null {
  if (!iso) {
    return null;
  }
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) {
    return null;
  }
  const purgeAt = date.getTime() + TRASH_PURGE_DAYS * MS_PER_DAY;
  return Math.max(0, Math.ceil((purgeAt - Date.now()) / MS_PER_DAY));
}

function TrashPage() {
  const { t, locale } = useI18n();
  const { db, drizzle, userId, staleTime } = useJournalData();
  const { restoreEntry, deleteEntryForever } = useEntryMutations();

  const deleted = useDeletedEntriesQuery({ db, drizzle, userId, staleTime });
  const [confirmEntry, setConfirmEntry] = createSignal<JournalEntry | null>(null);

  return (
    <PageContainer maxWidth="2xl" class="flex flex-col gap-4 pb-16">
      <header class="flex flex-col gap-1 pt-2">
        <h1 class="flex items-center gap-2 text-xl font-bold text-foreground">
          <Trash2 class="h-5 w-5 text-muted-foreground" />
          {t(msg`Trash`)}
        </h1>
        <p class="text-xs text-muted-foreground">
          {t(msg`Deleted entries stay here until you restore or permanently delete them.`)}
        </p>
      </header>

      <Show
        when={!deleted.isLoading}
        fallback={
          <div class="flex flex-col gap-3">
            <Skeleton class="h-24 w-full rounded-2xl" />
            <Skeleton class="h-24 w-full rounded-2xl" />
          </div>
        }
      >
        <Show
          when={(deleted.data ?? []).length > 0}
          fallback={
            <div data-testid="trash-empty" class="flex flex-1">
              <EmptyState
                icon={Trash2}
                title={t(msg`Trash is empty`)}
                description={t(
                  msg`Entries you delete will appear here so you can change your mind.`,
                )}
              />
            </div>
          }
        >
          <section class="flex flex-col gap-3" data-testid="trash-list">
            <For each={deleted.data ?? []}>
              {(entry) => (
                <article
                  class="flex flex-col gap-2 rounded-2xl border border-border/50 bg-card/70 p-4 shadow-sm backdrop-blur-sm"
                  data-testid="trash-item"
                >
                  <div class="flex items-center justify-between gap-2 text-xs text-muted-foreground">
                    <span class="capitalize">
                      {formatDayShort(entry.entry_date ?? "", locale())}
                    </span>
                    <span class="text-muted-foreground/60">
                      {t(msg`Deleted {when}`, {
                        when: formatRelativeTime(entry.deleted_at, locale()),
                      })}
                    </span>
                  </div>
                  <Show when={daysUntilPurge(entry.deleted_at) !== null}>
                    <p
                      class="text-[11px] font-medium text-muted-foreground/70"
                      data-testid="trash-purge-countdown"
                    >
                      {t(msg`Will be permanently deleted in {days} days`, {
                        days: daysUntilPurge(entry.deleted_at) ?? 0,
                      })}
                    </p>
                  </Show>
                  <Show when={entry.title}>
                    <h2 class="font-display text-base font-semibold text-foreground">
                      {entry.title}
                    </h2>
                  </Show>
                  <p class="line-clamp-3 font-serif text-sm italic leading-relaxed text-muted-foreground">
                    {preview(entry.body)}
                  </p>
                  <div class="flex justify-end gap-2 pt-1">
                    <Button
                      variant="ghost"
                      size="sm"
                      class="gap-1.5 text-muted-foreground hover:text-destructive"
                      data-testid="trash-delete-forever"
                      onClick={() => setConfirmEntry(entry)}
                    >
                      <Trash2 class="h-4 w-4" />
                      {t(msg`Delete forever`)}
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      class="gap-1.5"
                      data-testid="trash-restore"
                      onClick={() => {
                        void restoreEntry(entry.id);
                      }}
                    >
                      <RotateCcw class="h-4 w-4" />
                      {t(msg`Restore`)}
                    </Button>
                  </div>
                </article>
              )}
            </For>
          </section>
        </Show>
      </Show>

      <ConfirmDialog
        title={t(msg`Delete forever?`)}
        description={t(
          msg`This entry and its photos will be permanently deleted. This cannot be undone.`,
        )}
        variant="destructive"
        open={confirmEntry() !== null}
        onOpenChange={(open) => {
          if (!open) {
            setConfirmEntry(null);
          }
        }}
        onConfirm={() => {
          const entry = confirmEntry();
          if (entry) {
            void deleteEntryForever(entry.id);
          }
          setConfirmEntry(null);
        }}
      />
    </PageContainer>
  );
}

export const trashRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/trash",
  component: TrashPage,
});
